import React from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { saveAs } from 'file-saver';

import { sendRequest, isBeyondUnlocked, triggerEvent, eventOn, eventOff } from '../helpers/global.js';
import Storage from '../helpers/Storage.js';

import OverallRatingView from './charts/OverallRatingView';
import RedemptionsView from './charts/RedemptionsView';
import ConversionView from './charts/ConversionView';
import ContactBlock from './common/ContactBlock';
import DashboardFilters from './common/DashboardFilters';

import '../sass/components/Dashboard.scss';

const mapStoreToProps = (store) => {
  return {
    user: store.data.user,
    venues: store.data.venues || [],
    isMobile: store.setup.isMobile,
  }
};

class OverviewDashboard extends React.Component {

  constructor(props) {
    super(props);
    const filter = Storage.getData('dashboardFilter');
    this.state = {
      data: null,
      filter: {
        interval: 'weekly',
        period: 'custom',
        from: '',
        to: '',
        ...filter,
      },
    };
    this.contactBlock = null;
    this.sentEvents = [];
  }

  getContainer = () => {
    return document.querySelector('.dashboardContainer');
  }

  componentDidMount = () => {
    triggerEvent('logAmplitude', 'landing - view page');
    eventOn('scroll', this.onScroll, this.getContainer());
  }

  componentDidUpdate = (prevProps, prevState) => {
    if (this.props.venues !== prevProps.venues) {
      this.requestData();
    }
  }

  componentWillUnmount() {
    eventOff('scroll', this.onScroll, this.getContainer());
  }

  logScrollEvent = (event) => {
    if (!this.sentEvents.includes(event)) {
      this.sentEvents.push(event);
      triggerEvent('logAmplitude', event);
    }
  }

  onScroll = () => {
    if (this.contactBlock?.getBoundingClientRect()?.bottom < window.innerHeight) {
      this.logScrollEvent('landing - ad - consult with burpple - impressions');
    }
  }

  requestData = () => {
    sendRequest({
      method: 'dashboard/overview',
      type: 'GET',
      data: {
        ...this.state.filter,
        venue_ids: this.props.venues,
      },
      success: (data) => {
        this.setState({data});
      },
      error: (data) => {
      }
    });
  }

  onDownloadCsv = () => {
    sendRequest({
      method: 'dashboard/overview_csv',
      type: 'GET',
      data: {
        ...this.state.filter,
        venue_ids: this.props.venues,
      },
      success: (data) => {
        const csvData = new Blob([data], { type: 'text/csv;charset=utf-8;' });
        saveAs(csvData, 'overview.csv');
      },
      error: (data) => {
      }
    });
  }

  renderRevenue = (data) => {
    return (
      <div className='containterContent'>
        <div className='value'>${data.est_revenue}</div>
        <div className='name'>
          Estimated savings&#8203;/&#8203;revenue
          <div className='info'>
            <div className={classnames('tooltip', {
              'bottom': true,
              'center': this.props.isMobile,
            })}>
              Estimated savings/revenue = Total no of redemptions x Value of deal
            </div>
          </div>
        </div>
      </div>
    )
  }

  renderViews = (data) => {
    const beyond = isBeyondUnlocked(this.props.user, this.props.venues);
    return (
      <div className='containterContent'>
        <div className='value'>{data.page_views}</div>
        <div className='name'>
          Page views
          <div className='info'>
            <div className={classnames('tooltip', {
              'bottom': true,
              'center': this.props.isMobile && !beyond,
              'left': this.props.isMobile && beyond,
            })}>
              Refers to unique users who visited your venue listing on the burpple 
              mobile app & website
            </div>
          </div>
        </div>
      </div>
    )
  }

  renderWishlisted = (data) => {
    return (
      <div className='containterContent'>
        <div className='value'>{data.wishlisted}</div>
        <div className='name'>
          Wishlisted
          <div className='info'>
            <div className={classnames('tooltip', {
              'bottom': !this.props.isMobile,
              'center': this.props.isMobile,
            })}>
              Refers to users who bookmarked / favourited your restaurant, with the 
              hopes of visiting it later
            </div>
          </div>
        </div>
      </div>
    )
  }

  renderReviews = (data) => {
    const beyond = isBeyondUnlocked(this.props.user, this.props.venues);
    return (
      <div className='containterContent'>
        <div className='value'>{data.public_reviews}</div>
        <div className='name'>
          Public reviews
          <div className='info'>
            <div className={classnames('tooltip', {
              'bottom': !this.props.isMobile,
              'center': beyond ^ this.props.isMobile,
              'left': this.props.isMobile && beyond,
            })}>
              Refers to customer reviews posted on your venue listing
            </div>
          </div>
        </div>
      </div>
    )
  }

  renderRedemptions = (data) => {
    return (
      <div className='containterContent'>
        <div className='title'>
          Redemptions
          <div className='info'>
            <div className={classnames('tooltip', {
              'center': this.props.isMobile,
            })}>
              Based on the number of deals redeemed
            </div>
          </div>
        </div>
        <div className='name'>Total</div>
        <div className='value'>{data.redemptions.total}</div>
        <div className='chart'>
          <RedemptionsView data={data.redemptions}/>
        </div>
        <div className='details'>
          <button
            className='link'
            onClick={() => this.props.history.push('/beyond')}
          >View details</button>
        </div>
      </div>
    )
  }
  
  renderConversion = (data) => {
    return (
      <div className='containterContent'>
        <div className='title'>
          Conversion funnel
          <div className='info'>
            <div className='tooltip'>
              Shows the flow of beyond users and the steps required to make a deal redemption
            </div>
          </div>
        </div>
        <div className='name'>Conversion rate</div>
        <div className='value'>{data.conversion_funnel.rate}%</div>
        <div className='chart'>
          <ConversionView data={data.conversion_funnel}/>
        </div>
      </div>
    )
  }

  renderOverall = (data) => {
    return (
      <div className='containterContent'>
        <div className='title'>
          Overall rating
          <div className='info'>
            <div className='tooltip'>
              Based on the private feedback that beyond customers give, after they redeem a deal
            </div>
          </div>
        </div>
        <div className='chart'>
          <OverallRatingView
            total={data.overall_rating.total}
            percent={data.overall_rating.percent_rating}
          />
        </div>
        <div className='details'>
          <button
            className='link'
            onClick={() => this.props.history.push('/feedback')}
          >View details</button>
        </div>
      </div>
    )
  }

  renderContactBlock = () => {
    return (
      <div className='containterContent'>
        <ContactBlock
          onContact={() => triggerEvent('logAmplitude', 'landing - ad - consult with burpple - clicked')}
          elemRef={elem => this.contactBlock = elem}
        />
      </div>
    )
  }

  render = () => {
    const data = this.state.data;
    const beyond = isBeyondUnlocked(this.props.user, this.props.venues);
    return (
      <div className='dashboard'>
        <div className='dashboardInfo'>
          <div className='dashboardHeader'>
            <div className='dashboardTitle'>Welcome back!</div>
            <div className='dashboardSubtitle'>See how your restaurant is performing on Burpple</div>
          </div>
          <div className='dashboardControls'>
            <DashboardFilters
              withInterval
              value={this.state.filter}
              onOpen={key => {
                if (key === 'interval') {
                  triggerEvent('logAmplitude', 'landing - view monthly dropdown');
                } else if (key === 'period') {
                  triggerEvent('logAmplitude', 'landing - view date filter');
                }
              }}
              onChange={change => {
                if (change.interval) {
                  triggerEvent('logAmplitude', 'landing  - use monthly dropdown');
                } else if (change.period) {
                  triggerEvent('logAmplitude', 'landing - use date filter');
                }
                const filter = { ...this.state.filter, ...change };
                Storage.setData('dashboardFilter', {
                  ...Storage.getData('dashboardFilter'),
                  ...filter,
                });
                this.setState({filter}, this.requestData);
              }}
              onDownloadCsv={beyond ? this.onDownloadCsv : null}
            />
          </div>
        </div>
        <div className='dashboardContainer'>
          {data ?
            <div className={classnames('dashboardContent', {
              'column3': !beyond
            })}>
              {beyond ?
                <div className='dashboardBlock small'>{this.renderRevenue(data)}</div>
              : null}
              <div className='dashboardBlock small'>{this.renderViews(data)}</div>
              <div className='dashboardBlock small'>{this.renderWishlisted(data)}</div>
              <div className='dashboardBlock small'>{this.renderReviews(data)}</div>
              <div className={`dashboardBlock ${beyond ? 'medium' : 'huge'}`}>{this.renderRedemptions(data)}</div>
              {beyond ?
                <div className='dashboardBlock medium'>{this.renderConversion(data)}</div>
              : null}
              {beyond ?
                <div className='dashboardBlock medium'>{this.renderOverall(data)}</div>
              : null}
              <div className='dashboardBlock medium'>{this.renderContactBlock()}</div>
            </div>
          : null}
        </div>
      </div>
    )
  }

}

export default connect(mapStoreToProps)(OverviewDashboard);
